import {
  isPlayerSame,
  LineupModel,
  LineupPlayer,
} from '@collector/desktop-feature-lineups'
import { SubParticipant } from '@collector/sportsapi-types'

import { LConfig } from '../../configuration'

/**
 * Creates a default lineup model by grouping players by their position.
 * This is a fallback implementation when no specific lineup config is provided.
 */
export function createDefaultLineupModel(
  players: SubParticipant[],
): LineupModel<PropertyKey> {
  const lineup: LineupModel<PropertyKey> = {
    player: [...players],
  }

  return lineup
}

/**
 * Returns the lineup model sorted alphabetically by short name.
 * This is a fallback implementation when no specific sorting is provided.
 */
export function toSortedLineupModelDefault(
  lineup: LineupModel<PropertyKey>,
): LineupModel<PropertyKey> {
  const sortByShortName = (a: SubParticipant, b: SubParticipant): number =>
    (a.short_name || '').localeCompare(b.short_name || '')

  const sorted: LineupModel<PropertyKey> = {}
  Object.keys(lineup).forEach((key) => {
    sorted[key] = [...lineup[key]].sort(sortByShortName)
  })

  return sorted
}

/**
 * Checks if the position of a player has changed between the current and initial lineup.
 */
export function hasPositionChangedDefault(
  key: PropertyKey,
  lineup: LineupModel,
  initialLineup: LineupModel,
): boolean {
  const current = lineup[key]
  const initial = initialLineup[key]

  if (current.length !== initial.length) return true

  for (let i = 0; i < current.length; i++) {
    const playerA: LineupPlayer = current[i]
    const playerB: LineupPlayer = initial[i]

    const samePlayer = isPlayerSame(playerA, playerB)
    const sameShirtNr = playerA.shirt_nr === playerB.shirt_nr

    const same = samePlayer && sameShirtNr

    if (!same) return true
  }

  return false
}

export function createDefaultConfig(): LConfig {
  return {
    addPlayer: {},
    positions: {
      player: {
        sectionLabel: 'Subparticipants',
        showShirtNr: true,
        buttonLabel: 'Subparticipant',
      },
    },
  }
}
